<template>
    <div class="mt-10">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <h3 class="font-medium text-lg text-gray-700 select-none">
                Activity
            </h3>

            <div class="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0">
                <simple-dropdown :label="activitiesFilter | titlecase" v-model="activitiesFilter">
                    <template slot="buttonOptions">
                        <simple-dropdown-option v-for="option in activitiesFilterOptions"
                                                :key="option.value"
                                                :value="option.value"
                                                :button-class="option.value === activitiesFilter ? 'text-indigo-600' : ''">
                            {{ option.label }}
                        </simple-dropdown-option>
                    </template>
                </simple-dropdown>

                <button @click.prevent="showEditCreateForm(null, 'Create')"
                        class="flex items-center font-medium text-indigo-600 hover:text-indigo-800 transition duration-300 sm:ml-4">
                    <icon-plus-circle class="w-4 h-4 mr-2"></icon-plus-circle>
                    <span>New Note / Activity</span>
                </button>
            </div>
        </div>

        <div v-if="!loadingActivities && mutableActivities && mutableActivities.length > 0"
             :class="{'overflow-y-scroll overscroll-none scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-indigo-100': !showDropdown}"
             :style="!showDropdown? 'max-height: 390px' : ''">

            <ul class="border-dotted border-l-2 border-gray-300 mt-4">
                <li v-for="activity in filteredActivities" :key="activity.id">
                    <component :is="resolveActivityComponent(activity)"
                               :activity="activity"
                               :show-dropdown="showDropdown"
                               @edit-activity="showEditCreateForm(activity)"
                               @notify-sms="notifyViaSms(activity)"
                               @notify-email="notifyViaEmail(activity)"
                               @delete-activity="showActivityDeleteConfirmation(activity)"
                               @email-document="shareDocument"
                               @view-email="loadThread"
                    ></component>
                </li>
            </ul>
        </div>

        <div v-else-if="loadingActivities" class="flex justify-center mt-10 items-center font-normal text-md">
            <icon-refresh class="w-4 h-4 animate-spin text-indigo-600 mr-2"></icon-refresh>
            Loading Activities
        </div>

        <div v-else class="flex justify-center mt-10 items-center"> Ticket does not have any activity yet</div>

        <div v-if="!loadingActivities && filteredActivities && filteredActivities.length === 0 && activitiesFilter !== 'All Activities'"
             class="flex justify-center mt-10 items-center font-normal text-md">
            No activities of that type
        </div>

        <simple-modal :show="!!(emailThread && emailThread.id)" max-width="sm:max-w-3xl" @close="emailThread = {}"
                      :title="!!(emailThread && emailThread.draft_email) ? '' : 'Viewing Email'" title-class="font-normal"
                      :body-class="!!(emailThread && emailThread.draft_email) ? '' : 'px-2 py-4'"
                      :show-title="!(emailThread && emailThread.draft_email)">
            <view-thread :thread="emailThread"
                         :context="emailContext"
                         v-if="userHasMailbox"
                         @close="emailThread = {}"
                         @forward-thread="threadForwardCreated"
                         @clear-context="clearEmailContext">
            </view-thread>
        </simple-modal>

        <create-new-sms
            ref="activitySmsCreateForm"
            contactable="App\Ticket"
            :contactable-id="mutableTicket.id"
            @new-sms-sent="fetchActivities"
            category="maintenance"
        ></create-new-sms>

        <activity-edit-create-form v-if="activityEditCreateFormType !== null" :type="activityEditCreateFormType" :ticket="mutableTicket" :activity="selectedActivity"
                                   @close="showEditCreateForm(null, null)" @activity-added="afterActivityCreate" @activity-edited="afterActivityEdit">
        </activity-edit-create-form>
    </div>
</template>

<script>
import ComposeEmail from "../../../mixins/compose-mail";

export default {
    name: "TicketActivitiesList",

    mixins: [ComposeEmail],

    props: {
        ticket: {
            required: true,
            type: Object,
            description: ""
        },
        showDropdown: {
            required: false,
            type: Boolean,
            description: "Toggle showing edit/delete/options dropdown",
            default: true,
        },
    },

    data () {
        return {
            mutableTicket: this.ticket,
            ticketActivityEmailUrl: `/tickets/${this.ticket.id}/emails/create`,
            mutableActivities: [],
            loadingActivities: true,
            selectedActivity: null,
            activityEditCreateFormType: null,
            activitiesFilter: "All Activities",
            activitiesFilterOptions: [{
                label: "All Activities",
                value: "All Activities"
            }, {
                label: "Documents",
                value: "document"
            }, {
                label: "Emails",
                value: "email"
            }, {
                label: "Maintenance Requests",
                value: "maintenance requests"
            }, {
                label: "Service Provider",
                value: "service provider"
            }, {
                label: "SMSs",
                value: "sms"
            }, {
                label: "Ticket Changes",
                value: "ticket"
            }, {
                label: "User Actions",
                value: "user action"
            }],
        };
    },

    computed: {
        filteredActivities: {
            get: function () {
                let activities = this.activitiesFilter !== "All Activities" ?
                    this.mutableActivities.filter(this.filterOnType) : this.mutableActivities;
                return _.orderBy(activities, ["id"], ["desc"]);
            },
            set: function () {
                return null;
            }
        },
    },

    watch: {
        activitiesFilter: function () {
            this.filteredActivities = this.activitiesFilter !== "All Activities"
                ? this.mutableActivities.filter(this.filterOnType)
                : this.mutableActivities;
        },
    },

    mounted () {
        this.fetchActivities();

        let self = this;
        Bus.$on("refresh-activity-list", function () {
            self.fetchActivities();
        });

        Bus.$on("email-deleted", function (emailId) {
            let index = self.mutableActivities.findIndex(activity => activity.model_id === emailId && activity.model_type === "App\\Email");
            if (index >= 0) {
                self.mutableActivities.splice(index, 1);
            }
        });
    },

    methods: {
        fetchActivities () {
            this.loadingActivities = true;
            axios.get("/tickets/" + this.mutableTicket.id + "/activities")
            .then(response => {
                this.mutableActivities = response.data;
                this.loadingActivities = false;
            }).catch(() => {
                this.loadingActivities = false;
                toastr.error("Could Not Update Activity List");
            });
        },

        isActivity (name, activityType) {
            return name === _.lowerCase(activityType);
        },

        filterOnType (activity) {
            return activity.type === this.activitiesFilter;
        },

        showEditCreateForm (activity, type = "Edit") {
            // Use function to open or close form. Create, Edit or null
            this.selectedActivity = activity;
            this.activityEditCreateFormType = type;
        },

        afterActivityCreate (activity) {
            this.mutableActivities.unshift(activity);
            toastr.success("Activity Logged!");
        },

        afterActivityEdit (activity) {
            let index = this.mutableActivities.findIndex(a => a.id === activity.id);
            this.mutableActivities.splice(index, 1, activity);
            toastr.success("Activity Successfully Updated");
        },

        showActivityDeleteConfirmation (activity) {
            let self = this;
            swal({
                title: "Delete this activity?",
                text: activity.activity,
                showCancelButton: true
            }).then((result) => {
                if (result.value) {
                    self.deleteActivity(activity.id);
                }
            });
        },
        deleteActivity (activityId) {
            App.delete("/activities/" + activityId + "/delete", new AppForm({}))
            .then(() => {
                let index = this.mutableActivities.findIndex(activity => activity.id === activityId);
                this.mutableActivities.splice(index, 1);
                toastr.success("Activity Successfully Deleted!");
            }).catch((error) => {
                toastr.error("Could not delete Activity. Error: " + error.message);
            });
        },

        notifyViaSms (activity) {
            this.selectedActivity = activity;
            this.$refs.activitySmsCreateForm.showSMSCreateForm();
        },

        notifyViaEmail (activity = null, contact = null, document = null) {
            if (activity) {
                this.selectedActivity = activity;
                this.emailContext.activity = activity.id;
            }
            this.emailContext.model = {
                type: "ticket",
                id: this.ticket.id
            };

            let self = this;

            this.createEmailWithUrl(this.ticketActivityEmailUrl, contact ? contact.id : null, document ? document.id : null, function () {
                self.fetchActivities();
            });
        },

        shareDocument (document) {
            this.notifyViaEmail(null, null, document);
        },

        toggleActivityEditCreateForm () {
            this.showActivityEditCreateForm = !this.showActivityEditCreateForm;
        },

        resolveActivityComponent (activity) {
            switch (activity.type) {
                case "ticket":
                    return "ticket-activity";
                case "user action":
                    return "user-activity";
                case "email":
                    return "email-activity";
                case "sms":
                    return "sms-activity";
                case "service provider":
                    return "service-provider-activity";
                case "document":
                    return "document-activity";
                case "maintenance request link":
                    return "maintenance-request-activity";
            }
        }
    },
};
</script>
